// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { ID3EPolicy, IKnownGoodApp } from 'src/types/d3ePolicy';
import { AlertDialogComponent } from 'src/app/dialogs/alert/alert.component';
import { MatDialog } from '@angular/material/dialog';
import { PolicySafeAppComponent } from './policy-safe-app/policy-safe-app.component';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy } from '@ngneat/until-destroy';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-policy-known-good',
    templateUrl: './policy-known-good.component.html',
    styleUrls: ['./policy-known-good.component.scss'],
    standalone: false
})
export class PolicyKnownGoodComponent implements OnInit, OnChanges {
  @Input() d3ePolicy: ID3EPolicy;
  @Output() changed: EventEmitter<void> = new EventEmitter<void>();
  @ViewChildren(PolicySafeAppComponent) safeApps: QueryList<PolicySafeAppComponent>;

  constructor(
    public dialog: MatDialog,
    private translate: TranslateService
  ) { }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges): void {
  }

  collapseAll() {
    this.safeApps.forEach(safeApp => {
      safeApp.collapse();
    });
  }

  toggle(app: PolicySafeAppComponent) {
    this.collapseAll();
    app.expand();
  }

  onAddAppClick() {
    if (!this.d3ePolicy.knownGood) {
      this.d3ePolicy.knownGood = [];
    }
    this.collapseAll();
    this.d3ePolicy.knownGood.push({
      app: 0,
      displayName: '',
      enabled: false,
      strength: 0,
      subject: {
        C: '',
        CN: '',
        L: '',
        O: '',
        ST: ''
      },
      paths: [],
    });
    this.changed.emit();
    setTimeout(this.setSafeAppFocus.bind(this));
  }

  setSafeAppFocus() {
    this.safeApps.last.setDisplayNameFocus();
  }

  deleteApp(app: IKnownGoodApp) {
    const i = this.d3ePolicy.knownGood.indexOf(app);
    if (i === -1) {
      console.error('Unable to find app.', app);
      return;
    }
    const vals = this.translate.instant(['DELETE_SAFE_APP_TITLE', 'DELETE_SAFE_APP_CONTENT',
      'DELETE_SAFE_APP_CONTENT_VAR', 'YES', 'NO'], {
        displayName: this.d3ePolicy.knownGood[i].displayName
    });
    let message;
    if (this.d3ePolicy.knownGood[i].displayName.length > 0) {
      message = vals['DELETE_SAFE_APP_CONTENT_VAR'];
    } else {
      message = vals['DELETE_SAFE_APP_CONTENT'];
    }
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '450px',
      data: {
        title: vals['DELETE_SAFE_APP_TITLE'],
        content: message,
        rejectBtnName: vals['NO'],
        acceptBtnName: vals['YES'],
        showRejectBtn: true,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.d3ePolicy.knownGood.splice(i, 1);
        this.changed.emit();
      }
    });
  }

  onSetRecommendedClick() {
    let updated = false;
    this.d3ePolicy.knownGood.forEach(k => {
      if (k && k.app > 0 && !k.enabled) {
        k.enabled = true;
        updated = true;
      }
    });

    if (updated) {
      this.changed.emit();
    }
  }
}
